import { Chip, Tooltip } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { ROLE_LABEL } from '../../core/services/permissions';
import type { ProjectRole } from '../../core/types/data-models';

interface SharedProjectChipProps {
  /** Workspace that owns the project. */
  tenantName: string;
  role: ProjectRole;
  sharedBy?: { name: string | null; email: string } | null;
  size?: 'small' | 'medium';
}

/**
 * The single definition of the "this project belongs to someone else" marker.
 *
 * Used by the switcher, the app header and the project pages so all three read
 * identically. Carries an icon as well as colour, so the distinction survives a
 * glance and a monochrome screenshot rather than relying on hue alone.
 */
export function SharedProjectChip({
  tenantName,
  role,
  sharedBy,
  size = 'small',
}: SharedProjectChipProps) {
  const who = sharedBy?.name ?? sharedBy?.email;
  const tooltip = who
    ? `Shared with you by ${who} · ${tenantName} workspace`
    : `Shared with you from the ${tenantName} workspace`;

  return (
    <Tooltip title={tooltip}>
      <Chip
        size={size}
        icon={<PeopleAltIcon />}
        label={`${tenantName} · ${ROLE_LABEL[role]}`}
        className="shared-project-chip"
        variant="outlined"
        color="secondary"
        sx={{ maxWidth: 260 }}
      />
    </Tooltip>
  );
}
